import { TickDouble02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { Tabs } from "shared/components/BaseTabs";
import type { Tab } from "shared/components/BaseTabs/types";
import { NotificationTab } from "../types";

interface LayoutHeaderProps {
  unreadOnly: boolean;
  toggleUnreadOnly: () => void;
  tab: NotificationTab;
  onTabChange: (tab: NotificationTab) => void;
  unreadTotalCount: number;
  unreadMentionCount: number;
  unreadAssignedCount: number;
  // Messages plus Calls: escalated conversations (CRM-1438), missed calls,
  // AI agent escalations and follow-ups.
  unreadCommunicationCount: number;
  unreadPropertyShieldCount: number;
  showPropertyShieldTab: boolean;
  unreadPhoneNumbersCount: number;
  showPhoneNumbersTab: boolean;
  unreadProductsCount: number;
  showProductsTab: boolean;
  // Applications, Mentions, Assigned and Communications (BSCPF-6947).
  showsProductTabs: boolean;
  unreadRentalVerificationCount: number;
  unreadApplicationCount: number;
  isMarkingAllAsRead: boolean;
  onMarkAllAsRead: () => void;
}

const renderTabLabel = (label: string, count: number) => (
  <span>
    {label}{" "}
    {count ? (
      <span className="px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded-md font-bold">
        {count}
      </span>
    ) : (
      ""
    )}
  </span>
);

export const LayoutHeader = (props: LayoutHeaderProps) => {
  const {
    unreadOnly,
    toggleUnreadOnly,
    tab,
    onTabChange,
    unreadTotalCount,
    unreadMentionCount,
    unreadAssignedCount,
    unreadCommunicationCount,
    unreadPropertyShieldCount,
    showPropertyShieldTab,
    unreadPhoneNumbersCount,
    showPhoneNumbersTab,
    unreadProductsCount,
    showProductsTab,
    showsProductTabs,
    unreadRentalVerificationCount,
    unreadApplicationCount,
    isMarkingAllAsRead,
    onMarkAllAsRead
  } = props;

  const tabs: Tab<NotificationTab>[] = [
    ...(showsProductTabs
      ? [
          {
            value: NotificationTab.Application,
            label: renderTabLabel("Applications", unreadApplicationCount)
          },
          {
            value: NotificationTab.Mention,
            label: renderTabLabel("Mentions", unreadMentionCount)
          },
          {
            value: NotificationTab.Assigned,
            label: renderTabLabel("Assigned", unreadAssignedCount)
          }
        ]
      : []),
    ...(showPropertyShieldTab
      ? [
          {
            value: NotificationTab.PropertyShield,
            label: renderTabLabel("Property Shield", unreadPropertyShieldCount)
          }
        ]
      : []),
    ...(showPhoneNumbersTab
      ? [
          {
            value: NotificationTab.PhoneNumbers,
            label: renderTabLabel("Phone numbers", unreadPhoneNumbersCount)
          }
        ]
      : []),
    ...(showProductsTab
      ? [
          {
            value: NotificationTab.Products,
            label: renderTabLabel("Products", unreadProductsCount)
          }
        ]
      : []),
    {
      value: NotificationTab.RentalVerification,
      label: renderTabLabel(
        "Rental verifications",
        unreadRentalVerificationCount
      )
    },
    ...(showsProductTabs
      ? [
          {
            value: NotificationTab.Communication,
            label: renderTabLabel("Communications", unreadCommunicationCount)
          }
        ]
      : [])
  ];

  return (
    <div className="divide-y divide-alpha-40">
      <div className="flex items-center gap-8 p-6">
        <div className="flex flex-1 items-center gap-4 min-w-0">
          <h1 className="text-xl font-bold leading-8 text-content-1000">
            Notifications
          </h1>
          <Switch
            label="Only unread"
            size={SwitchSize.SM}
            checked={unreadOnly}
            onChange={() => toggleUnreadOnly()}
          />
        </div>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          className="shadow-xs"
          HugeIcon={TickDouble02Icon}
          disabled={unreadTotalCount === 0 || isMarkingAllAsRead}
          onClick={() => onMarkAllAsRead()}
        >
          Mark all as read
        </Button>
      </div>

      <div className="p-4 pb-0">
        <Tabs<NotificationTab>
          size="sm"
          tabStyle="line"
          fullWidthTabs={false}
          tabs={tabs}
          active={tab}
          onChange={(t) => onTabChange(t)}
        />
      </div>
    </div>
  );
};
